// Readings this device is holding for somebody else.
//
// WHAT IS BEING PREVENTED. A shift ends, the next operator signs in on the same
// docked iPad, and the queue still has readings keyed by the person who has gone
// home. There are three things a system can do with those, and two of them are
// wrong:
//
//   - Send them under whoever is signed in now. This is falsification. The whole
//     app rests on CapturedByUpn being the person who took the reading, and there
//     is no Person table to soften it — the UPN IS the attribution. So there is
//     NO "send these as me" control on this banner, and one must never be added.
//     (The server would catch it anyway: a body-supplied UPN that disagrees with
//     the token is overwritten and writes an ATTRIBUTION_MISMATCH event. But an
//     operator should never be offered the button in the first place.)
//   - Say nothing and keep them. The readings sit forever, the checks look taken,
//     and nobody knows. That is the silent loss this whole design exists against.
//   - Name the owner, state the one action that releases them, and offer a
//     discard that says whose work is being thrown away. That is this banner.
//
// Amber, not red: red is reserved for OUT and REJECTED. Held readings are not out
// of spec — they are unsent.
import { useState } from 'react';
import type { HeldItem } from '../../offline/queue';

// The focus ring is spelled out here rather than imported from OperatorShell:
// the shell imports this component, and importing it back would make a module
// cycle out of a one-line string.
const opFocus =
  'focus-visible:outline-hidden focus-visible:ring-4 focus-visible:ring-(--primary) ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-(--bg)';

export interface HeldBannerProps {
  /** The queued readings that belong to somebody other than the signed-in
   *  account. The ROWS, not a count: a discard has to name the row it destroys
   *  and the account that keyed it, and a number carries neither. */
  items: HeldItem[];
  /** The account that keyed them, when they all came from ONE. Null when they
   *  came from several — naming one of several would send the wrong person. */
  owner: string | null;
  /**
   * Deletes one held row. Takes the owner's UPN as well as the row id because
   * the queue refuses a discard whose owner does not match the row's stamp —
   * a list that re-ordered under a finger must not destroy the row next to the
   * one that was meant.
   */
  onDiscard: (queueId: number, ownerUpn: string) => Promise<void>;
}

/** What a held reading was, when the capture carried enough to say. A device
 *  that is offline cannot look up what run 412 was, which is why the queue
 *  stores this alongside the reading instead of an id to resolve later. */
function describe(item: HeldItem): string {
  const bits = [item.context.parameterName ?? item.context.parameterCode, item.context.runLabel]
    .filter((bit): bit is string => Boolean(bit))
    .join(' · ');
  return bits || `capture ${item.clientCaptureId.slice(0, 8)}`;
}

export function HeldBanner({ items, owner, onDiscard }: HeldBannerProps) {
  const [confirming, setConfirming] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const count = items.length;

  // The shell mounts this permanently, so it decides for itself whether it has
  // anything to say. Nothing held, nothing drawn.
  if (count === 0) return null;

  const who = owner ?? 'another account';
  const reading = count === 1 ? 'reading' : 'readings';

  // Each row is discarded under ITS OWN stamp, never under `owner`: when the held
  // items come from more than one account `owner` is null, and passing a single
  // name for all of them is exactly the mismatch the queue refuses.
  async function discardAll() {
    setConfirming(false);
    setFailure(null);
    try {
      for (const item of items) await onDiscard(item.queueId, item.upn);
    } catch (err: unknown) {
      setFailure(err instanceof Error ? err.message : 'The readings could not be discarded.');
    }
  }

  return (
    <section
      aria-label="Held readings"
      className="flex flex-none flex-wrap items-center gap-(--s3) border-b border-(--warn-line) bg-(--warn-bg) px-(--s4) py-(--s3)"
    >
      <div className="min-w-0 flex-1">
        <p className="m-0 text-(length:--fs-md) font-bold text-(--warn)">
          {count} {reading} on this device {count === 1 ? 'was' : 'were'} taken by {who}.
        </p>
        {/* The only thing that sends them, stated as an instruction rather than a
            status. An operator reading this at 05:40 needs to know what to do. */}
        <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--fg)">
          They will send when {who} signs in on this device. Nobody else can send them, because the
          record names the person who took the reading.
        </p>
        {/* Named, not just counted. "Four readings" is a number to dismiss; "net
            weight on Line 3" is a check somebody still owes. */}
        <ul className="m-0 mt-(--s1) flex list-none flex-wrap gap-(--s2) p-0 text-(length:--fs-sm) text-(--fg)">
          {items.slice(0, 4).map((item) => (
            <li key={item.queueId} className="font-mono">
              {describe(item)}
            </li>
          ))}
          {count > 4 ? <li className="font-mono">and {count - 4} more</li> : null}
        </ul>
        {failure ? (
          <p role="alert" className="m-0 mt-(--s1) text-(length:--fs-sm) font-bold text-(--fg)">
            {failure}
          </p>
        ) : null}
      </div>

      {!confirming ? (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className={`min-h-[56px] rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s4) text-(length:--fs-md) font-semibold text-(--fg) ${opFocus}`}
        >
          Discard {count} held {reading}
        </button>
      ) : (
        <div
          role="group"
          aria-label={`Confirm discarding ${count} held ${reading}`}
          className="flex flex-wrap items-center gap-(--s2)"
        >
          {/* The confirm names the owner. "Are you sure?" invites a reflex yes;
              "throw away Priya's four readings" is a different question. */}
          <span className="text-(length:--fs-sm) font-semibold text-(--fg)">
            Throw away {count} {reading} taken by {who}? They cannot be recovered and the checks stay
            owed.
          </span>
          {/* Ink, not red, even though this is the destructive one. On these
              screens red is a verdict about product; a red button would teach an
              operator to read it as a state rather than an action. The weight is
              carried by the sentence above it, which names whose work it is. */}
          <button
            type="button"
            onClick={() => void discardAll()}
            className={`min-h-[56px] rounded-(--radius) border border-(--border-strong) bg-(--primary) px-(--s4) text-(length:--fs-md) font-bold text-(--primary-fg) ${opFocus}`}
          >
            Discard them
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className={`min-h-[56px] rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s4) text-(length:--fs-md) font-semibold text-(--fg) ${opFocus}`}
          >
            Keep them
          </button>
        </div>
      )}
    </section>
  );
}

export default HeldBanner;
